<script lang="ts" setup>
// components
import Cards from '~/components/redesign/Cards';
import Forms from '~/components/redesign/Forms';
import AppPagination from '~/components/redesign/AppPagination.vue';
import AppBreadcrumbs from '~/components/redesign/AppBreadcrumbs.vue';

// composable
import useApiSeo from '~/composables/useApiSeo';

// utils
import get from 'lodash.get';
import scrollToTop from '~/utils/scrollToTop';
import {
  isString,
  capitalize,
  removeLocaleFromPath,
  convertArticleToCardsBlogProps,
} from '~/utils';

// constants
import { PostType } from '~/types';
import { PATHS } from '~/constants';

const route = useRoute();
const router = useRouter();
const { searchParams } = useRequestURL();

const page = ref(Number(searchParams.get('page')) || 1);
const category = removeLocaleFromPath(route.path).replaceAll('/', '');

const { data } = await useAsyncGql({
  operation: 'BlogArticlesPreviewsFiltered',
  variables: {
    first: 12,
    page,
    types: PostType.POST,
    category_slugs: [category],
  },
});

const previews = computed(() => get(data.value, 'blog_articles.data', []));
const meta = computed(() => get(data.value, 'blog_articles.paginatorInfo', {}));

const subscribeMessage = ref<string>();
const setSubscribeMessage = (message: string) => {
  subscribeMessage.value = message;
};
const isSubscribed = computed(() => isString(subscribeMessage.value));

const CATEGORY_SLUG_TO_PATH = {
  technical: PATHS.TECHNICAL,
  business: PATHS.BUSINESS,
} as const;

watch(page, async value => {
  await router.push({ query: { page: String(value) } });
  scrollToTop();
});

useApiSeo(category, 'Category');
</script>

<template>
  <div data-page-name="blog-category">
    <section class="articles">
      <div class="external-wrapper">
        <AppBreadcrumbs
          :items="[PATHS.BLOG, CATEGORY_SLUG_TO_PATH[category]]"
          overwrite
          accent="light"
        />

        <h1>{{ $t(capitalize(category)) }}</h1>

        <ul class="cards">
          <li v-for="preview of previews" :key="preview.id">
            <Cards.Blog
              v-bind="convertArticleToCardsBlogProps(preview)"
              orientation="vertical"
            />
          </li>
        </ul>
      </div>

      <AppPagination
        v-if="Number(meta.lastPage) > 1"
        v-model="page"
        :total-items="meta.total"
        :items-per-page="meta.perPage"
        :max-pages-shown="4"
      />
    </section>

    <section class="product-news">
      <div class="external-wrapper">
        <div class="inner-wrapper">
          <div :class="['subscribe-form', { hidden: isSubscribed }]">
            <h3>{{ $t('Stay_up_to_date') }}</h3>
            <Forms.Subscribe @change="setSubscribeMessage" />
          </div>

          <div :class="['subscribe-message', { hidden: !isSubscribed }]">
            <span v-html="subscribeMessage" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/position';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/fn';

.external-wrapper {
  @include size.wrapper(80rem);

  padding: 0 2rem;

  @include media.mobile {
    padding: 0 1rem;
  }
}

.inner-wrapper {
  @include size.wrapper(37rem);

  @include media.tablet {
    padding: 0 2rem;

    @include size.wrapper(100%);
  }

  @include media.mobile {
    padding: 0 1rem;
  }
}

.hidden {
  opacity: 0;
  pointer-events: none;
}

[data-page-name='blog-category'] {
  padding-top: 2rem;

  @include media.mobile {
    padding-top: 1rem;
  }

  section.articles {
    h1 {
      @include typo.h2;

      margin: 3rem 0 2rem;

      @include media.tablet {
        margin: 2rem 0 1.5rem;
      }

      @include media.mobile {
        margin: 1.5rem 0;
      }
    }

    ul.cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      align-items: stretch;
      gap: 2rem;

      @include media.tablet {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
      }

      @include media.mobile {
        grid-template-columns: 1fr;
      }
    }

    [data-component-name='AppPagination'] {
      margin-top: 5rem;

      @include media.tablet {
        margin-top: 2rem;
      }

      @include media.mobile {
        margin-top: 1.5rem;
      }
    }
  }

  section.product-news {
    padding: 5rem 0;
    margin-top: 10.25rem;
    background-color: fn.token('surf-cont-secondary');

    @include media.tablet {
      padding: 4rem 0;
      margin-top: 8rem;
    }

    @include media.mobile {
      padding: 1.5rem 0;
      margin-top: 5rem;
    }

    .external-wrapper,
    .subscribe-message {
      transition: opacity 0.15s ease;
    }

    .external-wrapper {
      .inner-wrapper {
        position: relative;

        .subscribe-form {
          h3 {
            @include typo.subtitle-1;

            margin-bottom: 1rem;
          }
        }

        .subscribe-message {
          @include position.absolute-full;
          @include flex.center-center;
          @include typo.subtitle-1;

          text-align: center;
        }
      }
    }
  }
}
</style>
